import { getToolName, isToolUIPart, type UIMessage } from 'ai';

/** A tool call still awaiting its result. */
export interface PendingToolCall {
  tool: string;
  toolCallId: string;
}

/**
 * Tool calls on the trailing assistant message that are still awaiting a
 * result (`input-streaming` / `input-available`, no output) — the dangling
 * state a `requires-action` interrupt (e.g. `askUserQuestion` awaiting its
 * `human()` callback) leaves on the transcript while the AI SDK run stays
 * open. Only the last assistant message can hold an unresolved interrupt,
 * so we scan just that one.
 *
 * This is the SSOT for detecting dangling tool calls on the AI SDK v6
 * `UIMessage[]` shape (parts with `type === 'tool-${name}'` and a `state`
 * discriminant). For the assistant-ui normalized `ThreadMessage` shape
 * (parts with `type === 'tool-call'`), see `isPendingToolCallPart` in
 * `hooks/isAwaitingInputPart.ts`.
 */
export function pendingToolCalls(messages: readonly UIMessage[]): PendingToolCall[] {
  const last = messages.at(-1);
  if (last?.role !== 'assistant') {
    return [];
  }
  return last.parts
    .filter(isToolUIPart)
    .filter((part) => part.state === 'input-streaming' || part.state === 'input-available')
    .map((part) => ({ tool: getToolName(part), toolCallId: part.toolCallId }));
}
